import { FC, useEffect, useRef, useState } from "react";
import { Modal, ModalBody } from "reactstrap";
import {
  IRecommendPlace,
  IUpdateRecommendPlace,
} from "../../interface/recommend-place";

interface IEditRecommendPlaceModal {
  show: boolean;
  place: IRecommendPlace;
  toggle: Function;
  onSubmit: (id: string, data: IUpdateRecommendPlace) => void;
}

const EditRecommendPlaceModal: FC<IEditRecommendPlaceModal> = ({
  show,
  place,
  toggle,
  onSubmit,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [reqUpdatePlace, setReqUpdatePlace] = useState<IUpdateRecommendPlace>({
    name: "",
    description: "",
    lat: 0,
    lng: 0,
    status: 1,
    picture: "",
  });
  const [imagePreview, setImagePreview] = useState<string>("");
  const [hasNewImage, setHasNewImage] = useState(false);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64String = event.target?.result as string;
        setReqUpdatePlace((prev) => ({
          ...prev,
          picture: base64String,
        }));
        setImagePreview(base64String);
        setHasNewImage(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setReqUpdatePlace((prev) => ({
      ...prev,
      picture: "",
    }));
    setImagePreview("");
    setHasNewImage(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Only include picture in payload if a new image was selected
    const payload: IUpdateRecommendPlace = {
      name: reqUpdatePlace.name,
      description: reqUpdatePlace.description,
      lat: reqUpdatePlace.lat,
      lng: reqUpdatePlace.lng,
      status: reqUpdatePlace.status,
    };

    if (hasNewImage) {
      payload.picture = reqUpdatePlace.picture;
    }

    onSubmit(place.id, payload);
  };

  useEffect(() => {
    if (show && place.id) {
      setReqUpdatePlace({
        name: place.name || "",
        description: place.description || "",
        lat: place.lat || 0,
        lng: place.lng || 0,
        status: place.status || 1,
        picture: "",
      });
      setImagePreview(place.picture || "");
      setHasNewImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [show, place]);

  return (
    <Modal size="lg" isOpen={show} toggle={() => toggle()} centered={false}>
      <div className="modal-title modal-header">
        <h5 className="modal-title">แก้ไขสถานที่แนะนำ</h5>
        <button
          type="button"
          onClick={() => toggle()}
          className="close"
          data-dismiss="modal"
          aria-label="Close"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <ModalBody>
        <form onSubmit={handleSubmit}>
          <div className="border-bottom mb-3">
            <div className="form-group row">
              <div className="col-12 col-md-3">
                <label className="required">ชื่อสถานที่</label>
              </div>
              <div className="col-12 col-md-9 col-lg-8">
                <input
                  className="form-control"
                  type="text"
                  required
                  value={reqUpdatePlace.name}
                  onChange={(e) =>
                    setReqUpdatePlace((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="form-group row">
              <div className="col-12 col-md-3">
                <label>คำอธิบาย</label>
              </div>
              <div className="col-12 col-md-9 col-lg-8">
                <textarea
                  className="form-control"
                  rows={4}
                  value={reqUpdatePlace.description}
                  onChange={(e) =>
                    setReqUpdatePlace((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="form-group row">
              <div className="col-12 col-md-3">
                <label className="required">ละติจูด</label>
              </div>
              <div className="col-12 col-md-9 col-lg-8">
                <input
                  className="form-control"
                  type="number"
                  step="any"
                  required
                  value={reqUpdatePlace.lat || ""}
                  onChange={(e) =>
                    setReqUpdatePlace((prev) => ({
                      ...prev,
                      lat: parseFloat(e.target.value) || 0,
                    }))
                  }
                />
              </div>
            </div>

            <div className="form-group row">
              <div className="col-12 col-md-3">
                <label className="required">ลองจิจูด</label>
              </div>
              <div className="col-12 col-md-9 col-lg-8">
                <input
                  className="form-control"
                  type="number"
                  step="any"
                  required
                  value={reqUpdatePlace.lng || ""}
                  onChange={(e) =>
                    setReqUpdatePlace((prev) => ({
                      ...prev,
                      lng: parseFloat(e.target.value) || 0,
                    }))
                  }
                />
              </div>
            </div>

            <div className="form-group row">
              <div className="col-12 col-md-3">
                <label>สถานะ</label>
              </div>
              <div className="col-12 col-md-9 col-lg-8">
                <select
                  className="form-control"
                  value={reqUpdatePlace.status}
                  onChange={(e) =>
                    setReqUpdatePlace((prev) => ({
                      ...prev,
                      status: parseInt(e.target.value),
                    }))
                  }
                >
                  <option value={1}>เปิดใช้งาน</option>
                  <option value={0}>ปิดใช้งาน</option>
                </select>
              </div>
            </div>

            <div className="form-group row">
              <div className="col-12 col-md-3">
                <label>รูปภาพ</label>
              </div>
              <div className="col-12 col-md-9 col-lg-8">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="form-control-file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                <small className="form-text text-muted">
                  เลือกรูปใหม่หากต้องการเปลี่ยน หรือเว้นไว้เพื่อใช้รูปเดิม
                </small>

                {imagePreview && (
                  <div className="mt-2">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: "200px",
                        height: "150px",
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                    />
                    <div className="mt-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={removeImage}
                      >
                        ลบรูปภาพ
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer px-0 pb-0">
            <button
              type="button"
              onClick={() => toggle()}
              className="btn btn-light"
            >
              ยกเลิก
            </button>
            <button type="submit" className="btn btn-primary">
              บันทึก
            </button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default EditRecommendPlaceModal;
